import { Role, rolesManipulator } from 'auth';
import { MeetingDateSort, MeetingTimeFilter } from 'compliance-api/generated.ts';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { MeetingsList } from '@/modules/common/components/meetings/MeetingsList.tsx';
import { MeetingsToolbar } from '@/modules/common/components/meetings/MeetingsToolbar.tsx';
import { AddUpdateMeetingModal } from '@/modules/common/components/modals/AddUpdateMeetingModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useGetMeetingsInfiniteQuery } from '@/modules/common/queries/meetings.ts';
import { DropdownMenuItem, ModalProviderContext, PageLayout, RadioButton, TabItem } from 'ui';

const sortOptions = [
  { label: 'Date (Earliest First)', value: MeetingDateSort.Asc },
  { label: 'Date (Latest First)', value: MeetingDateSort.Desc },
];

const Meetings = () => {
  const userRole = rolesManipulator.current_user_role;
  const isAdmin = userRole === Role.COMPLIANCE_ADMIN;
  const businessId = useGetCurrentBusinessId();
  const { openModal } = useContext(ModalProviderContext);
  const [searchValue, setSearchValue] = useState('');
  const [timeFilter, setTimeFilter] = useState<MeetingTimeFilter>(MeetingTimeFilter.Upcoming);
  const [dateSort, setDateSort] = useState<MeetingDateSort | undefined>();

  const handleSelectDateSort = useCallback((value: MeetingDateSort) => {
    setDateSort(value);
  }, []);

  const handleScheduleMeeting = useCallback(() => {
    openModal(AddUpdateMeetingModal);
  }, [openModal]);

  useEffect(() => {
    setSearchValue('');
    setTimeFilter(MeetingTimeFilter.Upcoming);
    setDateSort(undefined);
  }, [businessId]);

  const selectedDateSort =
    dateSort ??
    (timeFilter === MeetingTimeFilter.Past ? MeetingDateSort.Desc : MeetingDateSort.Asc);

  const functionalSortOptions = useMemo<DropdownMenuItem<ReactNode>[]>(
    () =>
      sortOptions.map(({ label, value }) => ({
        label: (
          <RadioButton items={[{ label, value }]} value={selectedDateSort} onChange={() => 0} />
        ),
        value,
        onClick: () => handleSelectDateSort(value),
      })),
    [handleSelectDateSort, selectedDateSort],
  );

  const selectedSort = useMemo(
    () => sortOptions.find(({ value }) => value === selectedDateSort) || sortOptions[0],
    [selectedDateSort],
  );

  const {
    hasNextPage,
    fetchNextPage,
    data: meetingsData,
    isLoading,
    isFetching,
  } = useGetMeetingsInfiniteQuery({
    page: 1,
    page_size: 18,
    search: searchValue,
    time_filter: timeFilter,
    date_sort: selectedDateSort,
  });

  const meetings = useMemo(
    () => (meetingsData?.pages || []).flatMap(({ data }) => data),
    [meetingsData],
  );

  const tabItems = useMemo<TabItem<MeetingTimeFilter>[]>(
    () => [
      {
        name: MeetingTimeFilter.Upcoming,
        label: 'Upcoming',
        container: <></>,
      },
      {
        name: MeetingTimeFilter.Past,
        label: 'Past',
        container: <></>,
      },
    ],
    [],
  );

  return (
    <PageLayout
      title="Meetings"
      tabs={tabItems}
      activeTab={timeFilter}
      setActiveTab={setTimeFilter}
      contentClassName="!pt-0"
    >
      <MeetingsToolbar
        searchValue={searchValue}
        selectedSortLabel={selectedSort.label}
        sortOptions={functionalSortOptions}
        isSearchLoading={isFetching && !isLoading}
        isAdmin={isAdmin}
        onSearchChange={setSearchValue}
        onScheduleMeeting={handleScheduleMeeting}
      />
      <MeetingsList
        meetings={meetings}
        isLoading={isLoading}
        isSearchUsed={!!searchValue}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </PageLayout>
  );
};

export default withPrivateAccess(Meetings, [Role.COMPLIANCE_ADMIN, Role.COMPLIANCE_USER]);
